import React, { useEffect, useState } from 'react';
import axios from 'axios';

// Local configurations and hooks
import { accordionItems, booleanOptions, panelConfig } from '../config/PdfToTxtConfig';
import { useHover } from '../hooks/useHover';
import { useToast } from '../hooks/useToast';

// Local components
import MainStructure from '../components/layout/MainStructure/MainStructure';
import SplitPanel from '../components/layout/MainStructure/PanelTwoColumns';
import InputSampleNumber from "../components/common/Input/InputNumber";
import InputString from '../components/common/Input/InputString';
import InputDrop from "../components/common/Input/InputDropdown";
import UploadSample from '../components/features/UploadForm/UploadForm';
import PreviewComponent from '../components/features/Preview/Preview';
import LoadingSample from '../components/common/Loader/Loader';
import AccordionSample from '../components/common/Accordion/Accordion';
import PanelSample from '../components/features/TitlePanel/TitlePanel';
import ButtonLoadSubmit from '../components/common/Button/ButtonSubmit';
import { config } from '../config/env.config';

// Services
import { apiClient } from '../services/apiClient';
import { fetchBlobFromSignedUrl, fetchTextFromSignedUrl, getSignedUrl, uploadFile } from '../utils/fileUpload';
import { handleApiError } from '../utils/errorHandler';
import { logError, logInfo } from '../utils/logging';
import { sanitizeUrl } from '../utils/url';

// API Configuration
const CONVERT_ENDPOINT = config.api.endpoints.convert;

interface CustomRequestBody {
  filename: string;
  email?: string;
  prima_pagina?: string;
  ultima_pagina?: string;
  fixed?: string;
  password?: string;
  mantieni_layout?: boolean;
  genera_html?: boolean;
}

type ConversionStatus = 'uploading' | 'converting' | null;

interface ConversionEstimate {
  minSeconds: number;
  expectedSeconds: number;
  maxSeconds: number;
}

const formatElapsedTime = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(remainingSeconds).padStart(2, '0')}`;
};

const formatDurationRange = (seconds: number): string => {
  if (seconds < 60) {
    return `${Math.max(1, Math.round(seconds))} sec`;
  }

  const minutes = seconds / 60;
  if (minutes < 10) {
    return `${minutes.toFixed(1)} min`;
  }

  return `${Math.round(minutes)} min`;
};

const getConversionEstimate = (fileSizeBytes: number): ConversionEstimate => {
  const fileSizeMb = fileSizeBytes / (1024 * 1024);

  if (fileSizeMb <= 5) {
    return { minSeconds: 1, expectedSeconds: 2, maxSeconds: 4 };
  }

  if (fileSizeMb <= 20) {
    return { minSeconds: 5, expectedSeconds: 8, maxSeconds: 15 };
  }

  if (fileSizeMb <= 60) {
    return { minSeconds: 15, expectedSeconds: 20, maxSeconds: 30 };
  }

  if (fileSizeMb <= 150) {
    return { minSeconds: 30, expectedSeconds: 60, maxSeconds: 120 };
  }

  return { minSeconds: 60, expectedSeconds: 240, maxSeconds: 540 };
};

// Helper function to fetch with size limit
const fetchWithSizeLimit = async (url: string, maxSize: number = 50000): Promise<string> => {
  return await fetchTextFromSignedUrl(url, maxSize);
};

// getSignedUrl and uploadFile moved to utils/fileUpload.ts

const initiateConversion = async (params: CustomRequestBody) => {
  return await apiClient.post(CONVERT_ENDPOINT, params, { timeout: 0 });
};

function Pdftotxt() {
  const [file, setFile] = useState<File | null>(null);
  const [inputFileName, setInputFileName] = useState('');
  const [primaPagina, setPrimaPagina] = useState<number | undefined>();
  const [ultimaPagina, setUltimaPagina] = useState<number | undefined>();
  const [fixed, setFixed] = useState<number | undefined>();
  const [password, setPassword] = useState('');
  const [mantieniLayout, setMantieniLayout] = useState(true);
  const [generaHtml, setGeneraHtml] = useState(false);
  const [loading, setLoading] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState('');
  const [filePreviewContent, setFilePreviewContent] = useState('');
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState<{loaded: number, total: number} | null>(null);
  const [uploadProgress, setUploadProgress] = useState<{loaded: number, total: number} | null>(null);
  const [conversionStatus, setConversionStatus] = useState<ConversionStatus>(null);
  const [conversionStartedAt, setConversionStartedAt] = useState<number | null>(null);
  const [conversionElapsedSeconds, setConversionElapsedSeconds] = useState(0);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const { isHovered, handleMouseEnter, handleMouseLeave } = useHover();
  const { showSuccess, showError, showWarning } = useToast();  // Add this line

  useEffect(() => {
    if (conversionStatus !== 'converting' || conversionStartedAt === null) {
      setConversionElapsedSeconds(0);
      return;
    }

    setConversionElapsedSeconds(Math.max(0, Math.floor((Date.now() - conversionStartedAt) / 1000)));

    const intervalId = window.setInterval(() => {
      setConversionElapsedSeconds(Math.max(0, Math.floor((Date.now() - conversionStartedAt) / 1000)));
    }, 1000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [conversionStartedAt, conversionStatus]);

  const handleFileChange = (newFile: File | null) => {
    setFile(newFile);
    if (newFile) {
      const safeName = newFile.name.split(/[\\/]/).pop() || newFile.name;
      setInputFileName(safeName);
    }
    // Reset states when new file is selected
    setFilePreviewContent('');
    setDownloadUrl('');
    setPreviewError(null);

    setDownloadProgress(null);
    setUploadProgress(null);
    setConversionStatus(null);
    setConversionStartedAt(null);
    setConversionElapsedSeconds(0);
  };


const fetchPreviewContent = async (url: string) => {
  setPreviewLoading(true);
  setPreviewError(null);
  
  try {
    // Always fetch 50KB preview
    const previewLength = 50000;
    const previewContent = await fetchWithSizeLimit(url, previewLength);
    
    if (previewContent) {
      // Always add preview indicator since we're limiting to 50KB
      setFilePreviewContent(
        previewContent + '\n\n... [Anteprima limitata - scarica per il contenuto completo]'
      );
    } else {
      throw new Error('Nessun contenuto ricevuto');
    }
    
  } catch (error) {
    console.error('Error fetching the file for preview');
    
    // Set error message but keep download available
    setPreviewError('Anteprima non disponibile. Il download è comunque disponibile.');
    setFilePreviewContent('');
    
  } finally {
    setPreviewLoading(false);
  }
};

  const handleConvertClick = async () => {
    if (!file) {
      showError('Nessun file selezionato');
      return;
    }
  
    setLoading(true);
    setPreviewError(null);
    setUploadProgress(null);
    setConversionStatus('uploading');
    setConversionStartedAt(null);
    setConversionElapsedSeconds(0);
    let currentStep: 'signed-url' | 'upload' | 'conversion' = 'signed-url';
    try {
      logInfo('PdfToText: avvio richiesta di upload');

      // Step 1: Get signed URL and upload file with progress tracking
      const signedUrl = await getSignedUrl(
        inputFileName,
        'application/pdf',
        config.storage.pdfBucketName
      );

      logInfo('PdfToText: sessione di upload ottenuta');

      currentStep = 'upload';
      await uploadFile(signedUrl, file, 'application/pdf', (progress) => {
        setUploadProgress(progress);
      });
      logInfo('PdfToText: upload completato');
      
      // Upload complete, now converting
      setConversionStatus('converting');
      setConversionStartedAt(Date.now());
      setUploadProgress(null);
      // Step 2: Initiate conversion
      const conversionParams: CustomRequestBody = {
        filename: inputFileName,
        prima_pagina: primaPagina ? String(primaPagina) : undefined,
        ultima_pagina: ultimaPagina ? String(ultimaPagina) : undefined,
        fixed: fixed ? String(fixed) : undefined,
        password,
        mantieni_layout: mantieniLayout,
        genera_html: generaHtml,
      };
  
      // Step 3: Initiate conversion (token refresh handled automatically by apiClient)
      currentStep = 'conversion';
      logInfo('PdfToText: avvio conversione');
      const { download_signed_url } = await initiateConversion(conversionParams);
      logInfo('PdfToText: conversione completata');
      setDownloadUrl(download_signed_url);
      // Conversion complete
      setConversionStatus(null);
      setConversionStartedAt(null);
      // Fetch preview in background - don't await
      fetchPreviewContent(download_signed_url);
  
    showSuccess('Conversione completata con successo');
      
} catch (error) {
  logError(`PdfToText: errore durante fase ${currentStep}`);

  const errorInfo = handleApiError(error);

  if (axios.isAxiosError(error) && error.response?.status === 400) {
    const backendMessage = String(error.response?.data?.error || error.response?.data?.message || '');

    if (backendMessage.toLowerCase().includes('password')) {
      showWarning(
        'Il PDF è protetto da password. Inserisci la password corretta e riprova.',
        'Password Richiesta',
        { duration: 500000, clickToClose: true }
      );
    } else if (
      currentStep === 'upload' &&
      (backendMessage.includes('Unsupported method') || backendMessage.includes('Content-Type'))
    ) {
      showError(
        'L’upload del file è fallito. Verifica la configurazione del proxy di upload e riprova.',
        'Errore Upload PDF',
        { duration: 500000, clickToClose: true }
      );
    } else {
      showError(errorInfo.message, errorInfo.title, { duration: 500000, clickToClose: true });
    }
  } else if (currentStep === 'signed-url') {
    showError(
      'Impossibile ottenere l’URL di upload del PDF. Riprova tra qualche istante.',
      'Errore Inizializzazione Upload',
      { duration: 500000, clickToClose: true }
    );
  } else if (currentStep === 'upload') {
    showError(
      'Il caricamento del PDF non è andato a buon fine. Riprova.',
      'Errore Upload PDF',
      { duration: 500000, clickToClose: true }
    );
  } else {
    showError(errorInfo.message, errorInfo.title, { duration: 500000, clickToClose: true });
  }
  
  setUploadProgress(null);
  setConversionStatus(null);
  setConversionStartedAt(null);
} finally {
      setLoading(false);
      setConversionStatus(null);
      setConversionStartedAt(null);
    }
  };

  const uploadPercentage = uploadProgress && uploadProgress.total > 0
    ? Math.round((uploadProgress.loaded / uploadProgress.total) * 100)
    : 0;
  const conversionEstimate = getConversionEstimate(file?.size ?? 0);
  const conversionMessage = conversionElapsedSeconds < 60
    ? 'Elaborazione del documento in corso...'
    : 'Elaborazione ancora in corso. Per file grandi possono servire alcuni minuti.';
  const isOverEstimatedTime = conversionElapsedSeconds > conversionEstimate.maxSeconds;

  const handleDownload = async () => {
    setIsDownloading(true);
    setDownloadProgress({ loaded: 0, total: 0 });
    let safeDownloadUrl = '';
    
    try {
      safeDownloadUrl = sanitizeUrl(downloadUrl);
      if (!safeDownloadUrl) {
        throw new Error('Invalid download URL');
      }
      const blob = await fetchBlobFromSignedUrl(safeDownloadUrl, (loaded, total) => {
        setDownloadProgress({
          loaded,
          total,
        });
      });

      const blobUrl = window.URL.createObjectURL(blob);
      const safeBlobUrl = sanitizeUrl(blobUrl, ['blob:']);
      if (!safeBlobUrl) {
        throw new Error('Invalid download URL');
      }
      if (!safeBlobUrl.startsWith('blob:')) {
        throw new Error('Invalid blob URL protocol');
      }

      const downloadLink = document.createElement('a');
      downloadLink.href = encodeURI(safeBlobUrl);
      downloadLink.setAttribute(
        'download',
        inputFileName.replace(/\.\w+$/, generaHtml ? '_converted.html' : '_converted.txt')
      );
      downloadLink.click();

      setTimeout(() => {
        window.URL.revokeObjectURL(blobUrl);
        setDownloadProgress(null);
      }, 1000);
      showSuccess('Il download è stato completato.',"",{ duration: 500000, clickToClose: true })
      
    } catch (error) {
      console.error('Download Failed');
      setDownloadProgress(null);
      showError('Errore durante il download. Riprova.',"",{ duration: 500000, clickToClose: true })
    } finally {
      setIsDownloading(false);
    }
  };

return (
  <MainStructure>
    <div className="accordion-wrap">
        <PanelSample config={panelConfig}>
          <div>
            <p>
              <strong>PDF to TXT</strong> è uno strumento di conversione versatile che accetta in input file PDF, consentendo di convertirli sia in formato <strong>TXT</strong> che <strong>HTML</strong>. L'output mantiene il layout e lo spaziamento fra parole del file originale, aggiustabili tramite parametri personalizzabili in fase di input.
            </p>
            <ul>
              <li>Il servizio è in versione <strong>Beta Test.</strong></li>
              <li>I tempi di attesa sono variabili e possono arrivare fino a diversi minuti per singolo file.</li>
              <li>La dimensione massima caricabile dipende dal backend e dall'infrastruttura disponibili.</li>
              <li>
                Per feedback contattare: <a href="mailto:it_dig_customsolutions@pwc.com" className="email-link">it_dig_customsolutions@pwc.com</a>
              </li>
              <li>
                <a href="https://pwcita.sharepoint.com/sites/IT-ADV-IT_AST_DIG_PA_COE/Shared%20Documents/Forms/AllItems.aspx?id=%2Fsites%2FIT%2DADV%2DIT%5FAST%5FDIG%5FPA%5FCOE%2FShared%20Documents%2FSviluppi%2F6%2E%20DIG%2FDocument%20Operational%20Toolbox%20%2D%20DOT&viewid=853f9744%2De714%2D4d52%2Db72e%2D8ac987ec1845&p=true&ct=1772100110668&or=OWA%2DNT%2DMail" target="_blank" rel="noopener noreferrer" className="email-link" style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                  <span className="Appkit4-icon icon-document-text-outline"></span>
                  Analisi funzionale
                </a>
              </li>
            </ul>
          </div>
        </PanelSample>
        <AccordionSample items={accordionItems} />
        <SplitPanel
          leftContent={
            <div>
              <div style={{ width: '75%'}}>
                <UploadSample onFileChange={handleFileChange} acceptFileType='.pdf' uploadInstruction='' maxFileSize={null} />
              </div>
              <div style={{ width:'73%',marginTop: '20px', marginRight: 'auto', display: 'flex', flexDirection: 'column', gap: '0px' }}>
                <InputSampleNumber title="Prima Pagina" value={primaPagina?.toString() || ''} onChange={value => setPrimaPagina(value ? parseFloat(value) : undefined)} />
                <InputSampleNumber title="Ultima Pagina" value={ultimaPagina?.toString() || ''} onChange={value => setUltimaPagina(value ? parseFloat(value) : undefined)} />
                <InputSampleNumber title="Fixed" value={fixed?.toString() ?? ''} onChange={value => setFixed(value ? parseFloat(value) : undefined)} />
                <InputString type="password" value={password} onChange={setPassword} title="Password" revealer={true} maxLength={256} />
                <InputDrop
                  placeholder="Mantieni layout"
                  value={mantieniLayout ? 'True' : 'False'}
                  onChange={(newValue) => setMantieniLayout(newValue === 'True')}
                  testData={booleanOptions}
                />
                <InputDrop
                  placeholder="Genera HTML"
                  value={generaHtml ? 'True' : 'False'}
                  onChange={(newValue) => setGeneraHtml(newValue === 'True')}
                  testData={booleanOptions}
                />
              </div>
              <div style={{ marginTop: '25px' }}>
                <ButtonLoadSubmit
                  name="Converti File"
                  loading={loading}
                  onClick={handleConvertClick}
                  disabled={!file}
                  style={{ width: '320px', marginTop: '20px' }}
                />
              </div>
            </div>
          }
          rightContent={
            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              {loading ? (
                <>
                  <LoadingSample style={{ marginTop: '30%' }} />
                  {(uploadProgress || conversionStatus) && (
                    <div style={{ 
                      marginTop: '30px', 
                      padding: '24px', 
                      backgroundColor: '#1a1a1a',
                      border: '1px solid #415385',
                      borderRadius: '12px',
                      width: '90%',
                      maxWidth: '450px',
                      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)'
                    }}>
                      <div style={{ marginBottom: '16px', fontSize: '16px', color: '#EBEBEB', textAlign: 'center', fontWeight: '500' }}>
                        {conversionStatus === 'uploading' ? 'Caricamento file in corso...' : 'Conversione in corso...'}
                      </div>
                      {uploadProgress && (
                        <>
                          <div style={{ width: '100%', height: '28px', backgroundColor: '#2a2a2a', borderRadius: '14px', overflow: 'hidden', marginBottom: '12px', border: '1px solid #415385' }}>
                            <div style={{ 
                              width: uploadProgress.total > 0 
                                ? `${(uploadProgress.loaded / uploadProgress.total) * 100}%` 
                                : '0%',
                              height: '100%',
                              background: 'linear-gradient(90deg, #50AD95 0%, #26776D 100%)',
                              transition: 'width 0.3s ease',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              position: 'relative'
                            }}>
                              <span style={{ color: '#EBEBEB', fontSize: '13px', fontWeight: 'bold', textShadow: '0 1px 3px rgba(0,0,0,0.5)', position: 'absolute', left: '50%', transform: 'translateX(-50%)' }}>
                                {uploadProgress.total > 0 ? `${uploadPercentage}%` : '0%'}
                              </span>
                            </div>
                          </div>
                          <div style={{ fontSize: '14px', color: '#9AA4BE', textAlign: 'center', marginTop: '8px' }}>
                            <span style={{ color: '#FB734D', fontWeight: '600' }}>
                              {(uploadProgress.loaded / (1024 * 1024)).toFixed(1)} MB
                            </span>
                            {uploadProgress.total > 0 && (
                              <span style={{ color: '#9AA4BE' }}>
                                {' '}/ {(uploadProgress.total / (1024 * 1024)).toFixed(1)} MB
                              </span>
                            )}
                          </div>
                          <div style={{ fontSize: '12px', color: '#9AA4BE', textAlign: 'center', marginTop: '12px', fontStyle: 'italic' }}>
                            {inputFileName}
                          </div>
                          <div style={{ fontSize: '13px', color: '#9AA4BE', textAlign: 'center', marginTop: '12px', lineHeight: 1.5 }}>
                            Al termine dell’upload la conversione partirà automaticamente.
                          </div>
                        </>
                      )}
                      {conversionStatus === 'converting' && !uploadProgress && (
                        <>
                          <div style={{ fontSize: '24px', color: '#FB734D', textAlign: 'center', fontWeight: '600', marginTop: '8px' }}>
                            {formatElapsedTime(conversionElapsedSeconds)}
                          </div>
                          <div style={{ fontSize: '14px', color: '#9AA4BE', textAlign: 'center', marginTop: '8px', lineHeight: 1.5 }}>
                            {conversionMessage}
                          </div>
                          <div style={{ fontSize: '13px', color: '#9AA4BE', textAlign: 'center', marginTop: '10px' }}>
                            Tempo stimato: {formatDurationRange(conversionEstimate.minSeconds)} - {formatDurationRange(conversionEstimate.maxSeconds)}
                          </div>
                          <div style={{ fontSize: '12px', color: '#9AA4BE', textAlign: 'center', marginTop: '8px' }}>
                            Stima basata sui benchmark interni; non rappresenta l’avanzamento reale del backend.
                          </div>
                          {isOverEstimatedTime && (
                            <div style={{ fontSize: '12px', color: '#FB734D', textAlign: 'center', marginTop: '10px', lineHeight: 1.5 }}>
                              La conversione sta richiedendo più tempo del previsto, ma potrebbe essere ancora in corso.
                            </div>
                          )}
                          <div style={{ fontSize: '12px', color: '#9AA4BE', textAlign: 'center', marginTop: '12px' }}>
                            {inputFileName && `${inputFileName} · `}{generaHtml ? 'Output HTML' : 'Output TXT'}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <>
                  {previewLoading && (
                    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                      <LoadingSample />
                      <p style={{ color: '#9AA4BE', margin: 0 }}>Caricamento anteprima...</p>
                    </div>
                  )}
                  {previewError && downloadUrl && (
                    <div style={{ marginTop: '20px', textAlign: 'center', padding: '20px' }}>
                      <p style={{ color: '#9AA4BE', marginBottom: '20px' }}>{previewError}</p>
                      <button
                        onClick={handleDownload}
                        disabled={isDownloading}
                        style={{
                          padding: '10px 20px',
                          background: isDownloading ? '#415385' : 'linear-gradient(90deg, #50AD95 0%, #26776D 100%)',
                          color: '#EBEBEB',
                          border: '1px solid #415385',
                          borderRadius: '4px',
                          cursor: isDownloading ? 'not-allowed' : 'pointer',
                          opacity: isDownloading ? 0.6 : 1,
                          transition: 'all 0.3s ease',
                          fontWeight: '500'
                        }}
                      >
                        {isDownloading ? 'Download in corso...' : 'Scarica file'}
                      </button>
                    </div>
                  )}
                  {filePreviewContent && !previewLoading && (
                    <div style={{ position: "relative", width: "100%", height: "100%" }}>
                      <PreviewComponent 
                        title={inputFileName.replace(/\.\w+$/, generaHtml ? '_converted.html' : '_converted.txt')} 
                        filePreview={filePreviewContent} 
                        generaHtml={generaHtml}
                        onDownload={handleDownload}
                        isDownloading={isDownloading}
                        downloadUrl={downloadUrl} 
                      />
                      {isDownloading && downloadProgress && (
                      <div style={{ 
                        position: "absolute",
                        top: "50%",
                        left: "45%",
                        transform: "translate(-50%, -50%)", // centra il box
                        zIndex: 10
                        }}>
                        <div style={{ 
                              backgroundColor: "#1a1a1a",
                              border: "1px solid #415385",
                              borderRadius: "12px",
                              minWidth: "400px",      
                              maxWidth: "500px",      
                              width: "90%",           
                              padding: "24px",
                              textAlign: "center",
                              boxShadow: "0 4px 16px rgba(0,0,0,0.4)"
                            }}>
                            <div style={{ marginBottom: "10px", fontSize: "14px", color: "#EBEBEB" }}>
                              Download in corso...
                            </div>
                            <div style={{ width: "100%", height: "24px", backgroundColor: "#2a2a2a", borderRadius: "12px", overflow: "hidden", marginBottom: "10px", border: "1px solid #415385" }}>
                              <div style={{ 
                                width: downloadProgress.total > 0 
                                  ? `${(downloadProgress.loaded / downloadProgress.total) * 100}%` 
                                  : "0%",
                                height: "100%",
                                background: "linear-gradient(90deg, #50AD95 0%, #26776D 100%)",
                                transition: "width 0.3s ease",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center"
                              }}>
                                <span style={{ color: "#EBEBEB", fontSize: "12px", fontWeight: "bold", textShadow: "0 1px 3px rgba(0,0,0,0.5)" }}>
                                  {downloadProgress.total > 0 
                                    ? `${Math.round((downloadProgress.loaded / downloadProgress.total) * 100)}%`
                                    : "0%"}
                                </span>
                              </div>
                            </div>
                            <div style={{ fontSize: "14px", color: "#9AA4BE", textAlign: "center" }}>
                              <span style={{ color: "#FB734D", fontWeight: "600" }}>
                                {(downloadProgress.loaded / (1024 * 1024)).toFixed(1)} MB
                              </span>
                              {downloadProgress.total > 0 && (
                                <span style={{ color: "#9AA4BE" }}>
                                  {" "}/ {(downloadProgress.total / (1024 * 1024)).toFixed(1)} MB
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          }
          isHovered={isHovered}
          handleMouseEnter={handleMouseEnter}
          handleMouseLeave={handleMouseLeave}
        />
      </div>
  </MainStructure>
);

}

export default Pdftotxt;
